import { Autocomplete, Box, Checkbox, TextField } from '@mui/material';
import { type MouseEvent, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import type { RoiId } from '~/domain/changes/ids';
import StructureSwatch from '~/components/Structures/StructureSwatch';
import { ValueRow } from '~/components/toolbar/shared/StageToggleRows';
import {
    computeGridAABB,
    roiRegionBounds,
} from '~/domain/services/imageService/gridGeometry';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import { voiBoundsState } from '~/state/atoms/toolsAtoms';
import { cmToMm, mmToCm } from '~/utils/units';

const DEFAULT_MARGIN_MM = 10;
const MAX_MARGIN_MM = 50;

/**
 * Places the registration box around the structures picked from the primary
 * row's structure set, with a margin. Each change of the structures or the
 * margin places the box again, and the user can still edit it. Picking no
 * structure leaves the box where it is.
 */
// shortcut: the list is read when this renders, so a structure added while the
// tray is open shows only after the next render; subscribe when that can happen
// in the registration step.
export default function RegionFromRois() {
    const intl = useIntl();
    const { environment } = useActiveFusionTarget();
    const setVoiBounds = useSetRecoilState(voiBoundsState);
    const structureSet = environment?.structureSet;
    const primaryGrid = environment?.image?.grid;
    const [picked, setPicked] = useState<{
        from: typeof structureSet;
        roiIds: RoiId[];
    }>({ from: undefined, roiIds: [] });
    const [marginMm, setMarginMm] = useState(DEFAULT_MARGIN_MM);
    const [search, setSearch] = useState('');

    if (!structureSet || !primaryGrid) {
        return null;
    }
    const rois = structureSet.structures.filter(
        (roi) => !roi.isPointRoi && !roi.isEmpty
    );
    if (rois.length === 0) {
        return null;
    }
    // A pick belongs to one structure set.
    const roiIds = picked.from === structureSet ? picked.roiIds : [];

    const placeBox = (pickedIds: RoiId[], margin: number) => {
        const boxes = pickedIds.flatMap((roiId) => {
            const box = structureSet
                .getRoi(roiId)
                ?.texture?.contentBoundingBox.toAxisAlignedBoundingBox();
            return box?.min && box.max
                ? [{ worldMin: box.min, worldMax: box.max }]
                : [];
        });
        const bounds = roiRegionBounds(
            boxes,
            margin,
            computeGridAABB(primaryGrid)
        );
        if (bounds) {
            setVoiBounds(bounds);
        }
    };

    const label = intl.formatMessage({ id: 'registration.regionFromRois' });
    const pickedNames = rois
        .filter((roi) => roiIds.includes(roi.roiId))
        .map((roi) => roi.name)
        .join(', ');
    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
        >
            <Autocomplete
                multiple
                disableCloseOnSelect
                disableClearable
                size="small"
                fullWidth
                options={rois}
                value={rois.filter((roi) => roiIds.includes(roi.roiId))}
                getOptionLabel={(roi) => roi.name}
                // A search outlasts a pick, so its other results stay at hand.
                inputValue={search}
                onInputChange={(_, value, reason) => {
                    if (reason === 'input') {
                        setSearch(value);
                    }
                }}
                onClose={() => setSearch('')}
                isOptionEqualToValue={(a, b) => a.roiId === b.roiId}
                onChange={(_, value) => {
                    const pickedIds = value.map((roi) => roi.roiId);
                    setPicked({ from: structureSet, roiIds: pickedIds });
                    placeBox(pickedIds, marginMm);
                }}
                noOptionsText={intl.formatMessage({
                    id: 'structure.noSearchMatch',
                })}
                data-testid="registration-region-rois"
                // The picks read in the field's placeholder, which keeps the
                // field to one line and gives way to a search being typed.
                renderTags={() => null}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={pickedNames || label}
                        inputProps={{
                            ...params.inputProps,
                            'aria-label': label,
                        }}
                    />
                )}
                renderOption={({ key: _key, ...props }, roi, { selected }) => (
                    // By id: a name need not be unique in a set.
                    <Box
                        component="li"
                        {...props}
                        key={roi.roiId}
                        sx={{ fontSize: 12, gap: 0.75 }}
                    >
                        <Checkbox
                            size="small"
                            checked={selected}
                            sx={{ p: 0 }}
                        />
                        <StructureSwatch
                            color={`rgb(${roi.colorRgb8.R}, ${roi.colorRgb8.G}, ${roi.colorRgb8.B})`}
                        />
                        {roi.name}
                    </Box>
                )}
                ListboxProps={{ sx: { maxHeight: 200 } }}
                slotProps={{
                    // The list is portaled, but React still bubbles its presses
                    // to the tray that holds the field.
                    popper: {
                        onClick: (e: MouseEvent) => e.stopPropagation(),
                        onMouseDown: (e: MouseEvent) => e.stopPropagation(),
                        sx: { zIndex: 1500 },
                    },
                }}
            />
            <ValueRow
                labelId="registration.regionMargin"
                tooltipId="registration.regionMarginTooltip"
                value={mmToCm(marginMm)}
                onValueChange={(cm) => {
                    const margin = cmToMm(cm);
                    setMarginMm(margin);
                    placeBox(roiIds, margin);
                }}
                min={0}
                max={mmToCm(MAX_MARGIN_MM)}
                step={0.1}
                snapToStep
                precision={1}
                unit="cm"
                dataTestId="registration-region-margin-cm"
            />
        </Box>
    );
}
